import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronDown, Users } from 'lucide-react';
import {
  HOME_BOOKING_EVENT_TYPES,
  buildOffertUrl,
  saveHomeBookingIntent,
  type HomeBookingEventType,
} from '../../utils/homeBookingIntent';
import { formatDateLocal } from '../../utils/dateUtils';
import {
  trackHomeBookingBarCtaClick,
  trackHomeBookingBarDateSelect,
  trackHomeBookingBarEventSelect,
} from '../../utils/siteAnalytics';

const HomeBookingBar: React.FC = () => {
  const navigate = useNavigate();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [date, setDate] = useState('');
  const [eventType, setEventType] = useState<HomeBookingEventType | ''>('');
  const todayYmd = useMemo(() => formatDateLocal(new Date()), []);

  const dateLabel = date
    ? new Date(date + 'T12:00:00').toLocaleDateString('sv-SE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : 'Välj datum';

  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el || typeof el.showPicker !== 'function') return;
    try {
      el.showPicker();
    } catch {
      // Native date UI används som fallback
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const intent = {
      date: date || undefined,
      event: eventType || undefined,
      dateUndecided: !date,
    };
    saveHomeBookingIntent(intent);
    trackHomeBookingBarCtaClick({
      hasDate: Boolean(date),
      eventType: eventType || null,
    });
    navigate(buildOffertUrl(intent));
  };

  return (
    <section
      id="home-booking-bar"
      className="relative z-20 mt-4 md:-mt-36 px-4 pb-6 md:pb-4"
      aria-label="Få ett personligt förslag"
    >
      <form
        onSubmit={onSubmit}
        className="mx-auto max-w-4xl rounded-none border border-white/35 bg-slate-800/95 shadow-[0_0_0_1px_rgba(168,85,247,0.28),0_0_48px_rgba(147,51,234,0.22),0_18px_50px_rgba(0,0,0,0.55)] backdrop-blur-md"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-row md:items-stretch">
          <div className="relative border-b border-white/15 sm:border-r md:flex-1 md:border-b-0 md:border-r md:border-white/15">
            <div className="relative flex w-full items-center gap-3 px-4 py-3 md:py-4">
              <div className="pointer-events-none flex min-w-0 flex-1 items-center gap-3">
                <Calendar className="shrink-0 text-purple-300" size={22} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-300">När är festen?</p>
                  <p
                    className={`truncate text-base font-semibold ${
                      date ? 'text-white' : 'text-slate-400'
                    }`}
                  >
                    {dateLabel}
                  </p>
                </div>
                <ChevronDown className="shrink-0 text-slate-500" size={18} aria-hidden />
              </div>
              <input
                ref={dateInputRef}
                type="date"
                min={todayYmd}
                value={date}
                onChange={(e) => {
                  const next = e.target.value;
                  setDate(next);
                  if (next) trackHomeBookingBarDateSelect(next);
                }}
                onClick={openDatePicker}
                className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                aria-label="Välj datum för festen"
              />
            </div>
          </div>

          <div className="relative border-b border-white/15 md:flex-1 md:border-b-0 md:border-r md:border-white/15">
            <div className="relative flex w-full items-center gap-3 px-4 py-3 md:py-4">
              <div className="pointer-events-none flex min-w-0 flex-1 items-center gap-3">
                <Users className="shrink-0 text-purple-300" size={22} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-300">Vad planerar du?</p>
                  <p
                    className={`truncate text-base font-semibold ${
                      eventType ? 'text-white' : 'text-slate-400'
                    }`}
                  >
                    {eventType || 'Välj event'}
                  </p>
                </div>
                <ChevronDown className="shrink-0 text-slate-500" size={18} aria-hidden />
              </div>
              <select
                value={eventType}
                onChange={(e) => {
                  const next = e.target.value as HomeBookingEventType | '';
                  setEventType(next);
                  if (next) trackHomeBookingBarEventSelect(next);
                }}
                className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                aria-label="Välj eventtyp"
              >
                <option value="" className="bg-slate-900 text-slate-300">
                  Välj event
                </option>
                {HOME_BOOKING_EVENT_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-slate-900 text-white">
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center p-3 sm:col-span-2 md:col-auto md:p-2 md:pl-3 md:pr-3">
            <button
              type="submit"
              className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-none bg-purple-600 px-5 py-3.5 text-sm font-medium text-white transition-colors hover:bg-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 md:w-auto"
            >
              Få ett personligt förslag
            </button>
          </div>
        </div>
      </form>
    </section>
  );
};

export default HomeBookingBar;
